/**
 * Options for {@link initScoutSession}.
 */
interface IScoutSessionOptions {
  /** Build-time public path prefix; non-empty only in scout builds. */
  publicPathPrefix: string;
  /** Build's git sha (7 chars). */
  gitVersion: string;
  /** Injectable for tests; defaults to window.sessionStorage. */
  storage?: Storage;
  /** Injectable for tests; defaults to window.history. */
  history?: History;
}

/**
 * Base path a scout build is served under (`/scout/<sha7>` or
 * `/scout/dev/<sha7>`), or undefined
 * outside scout builds. Single source for the router basename and the
 * scoutReturnPath restore — both must agree with webpack's baked publicPath.
 *
 * @param publicPathPrefix - Build-time prefix; `'scout/'` (trailing slash) in scout builds.
 * @param gitVersion - Build's git sha (7 chars).
 * @returns The basename, or undefined when not a scout build.
 */
export function getScoutBasename(publicPathPrefix: string, gitVersion: string): string | undefined {
  return publicPathPrefix ? `/${publicPathPrefix}${gitVersion}` : undefined;
}

/**
 * The build's path under `/scout/` (`<sha7>`, or `dev/<sha7>` for a dev-backend
 * preview), which index.html's escape net redirects back to.
 *
 * @param publicPathPrefix - Build-time prefix; `'scout/'` or `'scout/dev/'` in scout builds.
 * @param gitVersion - Build's git sha (7 chars).
 * @returns The path after `/scout/`.
 */
function scoutBuildPath(publicPathPrefix: string, gitVersion: string): string {
  return `${publicPathPrefix}${gitVersion}`.replace(/^scout\//, '');
}

/**
 * Strips the scout base path from a raw `window.location.pathname`, yielding
 * the app-relative path the router and nav code expect. Router-provided
 * `useLocation()` paths are already basename-relative; only raw window reads
 * need this.
 *
 * @param pathname - Raw pathname, possibly carrying the scout base path.
 * @param publicPathPrefix - Build-time prefix; `'scout/'` (trailing slash) in scout builds.
 * @param gitVersion - Build's git sha (7 chars).
 * @returns The pathname without the scout base path.
 */
export function stripScoutBasename(
  pathname: string,
  publicPathPrefix: string,
  gitVersion: string,
): string {
  const basename = getScoutBasename(publicPathPrefix, gitVersion);
  if (basename && pathname.startsWith(`${basename}/`)) {
    return pathname.slice(basename.length);
  }
  if (basename && pathname === basename) {
    return '/';
  }
  return pathname;
}

/**
 * Bootstraps the scout preview session on boot of a scout build.
 *
 * Scout builds are served under `/scout/<sha>/`. On load this re-stamps the
 * `scoutSha` flag (re-arming the escape net the index.html snippet clears
 * before bouncing, keeping the bounce loop-safe) and restores any stashed
 * route. Convention: `scoutReturnPath` always stores the app-relative path
 * (no `/scout/<sha>` prefix, with search + hash); this function prepends its
 * own base path on restore.
 *
 * Does nothing outside scout builds (empty `publicPathPrefix`). All storage
 * access is wrapped in a try/catch since storage can throw in private mode.
 *
 * @param options - Build-time globals and injectable browser API boundaries.
 */
export function initScoutSession({
  publicPathPrefix,
  gitVersion,
  storage = globalThis.window.sessionStorage,
  history = globalThis.window.history,
}: IScoutSessionOptions): void {
  if (!publicPathPrefix) {
    return;
  }

  try {
    storage.setItem('scoutSha', scoutBuildPath(publicPathPrefix, gitVersion));

    const returnPath = storage.getItem('scoutReturnPath');
    if (returnPath !== null) {
      storage.removeItem('scoutReturnPath');
      /*
       * Same shape guard as the routing-brain snippet in index.html: only a
       * same-origin absolute path ("/x" — never "//host/x" or "/\host\x",
       * which browsers treat as protocol-relative) may be restored. The
       * basename prefix already pins the result under /scout/<sha>, so this
       * is defense-in-depth so both consumers of scoutReturnPath enforce
       * the contract rather than assume it.
       */
      if (
        returnPath.charAt(0) !== '/' ||
        returnPath.charAt(1) === '/' ||
        returnPath.charAt(1) === '\\'
      ) {
        return;
      }
      history.replaceState(
        null,
        '',
        `${getScoutBasename(publicPathPrefix, gitVersion)}${returnPath}`,
      );
    }
  } catch {
    // sessionStorage unavailable (private mode edge cases): skip persistence.
  }
}
